import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Mail, Accessibility } from 'lucide-react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
} from '../dialog/dialog.js';
import { Switch } from '../switch/switch.js';

const STORAGE_KEY = 'agentbrush:preferences';

export interface QuickSettingsPreferences {
  notifications: boolean;
  emailUpdates: boolean;
  reducedMotion: boolean;
}

const DEFAULT_PREFS: QuickSettingsPreferences = {
  notifications: true,
  emailUpdates: true,
  reducedMotion: false,
};

export function readQuickSettings(): QuickSettingsPreferences {
  if (typeof localStorage === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<QuickSettingsPreferences>;
    return {
      notifications: parsed.notifications ?? DEFAULT_PREFS.notifications,
      emailUpdates: parsed.emailUpdates ?? DEFAULT_PREFS.emailUpdates,
      reducedMotion: parsed.reducedMotion ?? DEFAULT_PREFS.reducedMotion,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writeQuickSettings(prefs: QuickSettingsPreferences) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* storage full or blocked — swallow */
  }
}

interface QuickSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function QuickSettingsDialog({ open, onClose }: QuickSettingsDialogProps) {
  const { t } = useTranslation('account');

  // Local draft — discarded on Cancel, persisted on Save
  const [draft, setDraft] = useState<QuickSettingsPreferences>(DEFAULT_PREFS);

  // Re-hydrate from storage each time the dialog opens
  useEffect(() => {
    if (open) {
      setDraft(readQuickSettings());
    }
  }, [open]);

  const handleSave = useCallback(() => {
    writeQuickSettings(draft);
    onClose();
  }, [draft, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogPanel className="max-w-md">
        <DialogClose onClick={handleCancel} label={t('quickSettings.close')} />

        <div className="px-5 pt-5 pb-3">
          <DialogTitle>{t('quickSettings.title')}</DialogTitle>
          <DialogDescription>{t('quickSettings.description')}</DialogDescription>
        </div>

        <DialogBody className="space-y-4">
          <Row
            icon={Bell}
            tint="text-accent-cyan"
            label={t('quickSettings.notifications.label')}
            description={t('quickSettings.notifications.description')}
            checked={draft.notifications}
            onChange={(v) => setDraft((d) => ({ ...d, notifications: v }))}
          />
          <Row
            icon={Mail}
            tint="text-accent-blue"
            label={t('quickSettings.emailUpdates.label')}
            description={t('quickSettings.emailUpdates.description')}
            checked={draft.emailUpdates}
            onChange={(v) => setDraft((d) => ({ ...d, emailUpdates: v }))}
          />
          <Row
            icon={Accessibility}
            tint="text-accent-lime"
            label={t('quickSettings.reducedMotion.label')}
            description={t('quickSettings.reducedMotion.description')}
            checked={draft.reducedMotion}
            onChange={(v) => setDraft((d) => ({ ...d, reducedMotion: v }))}
          />
        </DialogBody>

        <DialogFooter>
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center justify-center rounded-md border border-white/10 bg-white/[0.03] px-4 py-2 text-small font-medium text-primary transition-colors hover:border-white/20 hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            {t('quickSettings.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            data-testid="quick-settings-save"
            className="inline-flex items-center justify-center rounded-md bg-accent-lime px-4 py-2 text-small font-semibold text-canvas transition-[filter,transform] duration-150 hover:brightness-105 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            {t('quickSettings.save')}
          </button>
        </DialogFooter>
      </DialogPanel>
    </Dialog>
  );
}

interface RowProps {
  icon: typeof Bell;
  tint: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function Row({ icon: Icon, tint, label, description, checked, onChange }: RowProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-white/[0.04] bg-white/[0.02] p-3">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/[0.06] bg-white/[0.03]">
          <Icon className={`h-4 w-4 ${tint}`} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-small font-medium text-primary">{label}</p>
          <p className="mt-0.5 text-caption leading-relaxed text-secondary">{description}</p>
        </div>
      </div>
      <div className="pt-0.5">
        <Switch checked={checked} onChange={onChange} aria-label={label} />
      </div>
    </div>
  );
}
